<template>
  <Slideover
title="Add New Stock Item" :open="open" :actions="[
    {
      text: 'Save',
      theme: 'primary',
      disabled: isSubmitting || !store.isOnline,
      method: handleSubmit
    }
  ]" @close="close">
    <form class="space-y-6" @submit.prevent="handleSubmit">
      <!-- Basic Information -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label class="block text-sm font-medium text-gray-700">Item No *</label>
          <input
v-model="form.ITEMNO" type="text" required
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Enter item number" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700">Area *</label>
          <input
v-model="form.AREA" type="text" required
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Enter area" />
        </div>
      </div>

      <!-- Quantity Row -->
      <div class="grid grid-cols-1 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-700">Quantity</label>
          <input
v-model.number="form.Quantity" type="number"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Enter quantity (optional)" />
        </div>
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium text-gray-700">Description *</label>
        <textarea
v-model="form.MEMO" rows="3" required
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          placeholder="Enter Description notes"></textarea>
      </div>
    </form>
  </Slideover>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import Slideover from '@/components/partials/Slideover.vue'
import useStockTake from '@/stores/stocktakestore'
import useToasterStore from '@/stores/toaster'

interface Props {
  open: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['close'])

const store = useStockTake()
const toaster = useToasterStore()
const isSubmitting = ref(false)

// Form data
const form = ref({
  ITEMNO: '',
  Quantity: null as number | null,
  AREA: store.enteredArea || '',
  PGROUP: '',
  GRPGROUP: '',
  RECID: '',
  MEMO: '',
})


// Stop polling when form opens, restart when closes
watch(() => props.open, (newOpen) => {
  store.setFormOpen(newOpen)
  if (newOpen) {
    store.stopPolling()
    resetForm()
  } else {
    store.startPolling()
  }
})

function resetForm() {
  form.value = {
    ITEMNO: '',
    Quantity: null as number | null,
    AREA: store.enteredArea || '',
    PGROUP: '',
    GRPGROUP: '',
    RECID: '',
    MEMO: '',
  }
}

async function handleSubmit() {
  if (isSubmitting.value || !store.isOnline) return

  isSubmitting.value = true

  try {
    const depot = store.selectedDepot.prefix.toUpperCase()

    // Prepare item data matching the required structure
    const itemData = {
      Depot: depot,
      AREA: form.value.AREA || '',
      ITEMNO: form.value.ITEMNO,
      Quantity: form.value.Quantity,
      MEMO: form.value.MEMO || '',
      RECID: form.value.RECID || '',
      Status: "1",
      First_Counter: store.counter1 || '',
      Second_Counter: store.counter2 || '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // Include optional fields only if they have values
      ...(form.value.PGROUP && { PGROUP: form.value.PGROUP }),
      ...(form.value.GRPGROUP && { GRPGROUP: form.value.GRPGROUP }),
    }

    // Remove null/undefined values
    const withoutNulls = Object.fromEntries(
      Object.entries(itemData).filter(([, v]) => v !== null && v !== undefined && v !== '')
    )

    const newItem = await store.duplicateStockItem(String(depot), withoutNulls)

    // Set syncing status for the new item if we have its ID
    let newItemId = null
    if (newItem?.data?.id) {
      newItemId = newItem.data.id
      await store.setSyncingItem(newItemId, true)
    }

    // Show success popup
    if (newItem?.data) {
      const createdItem = newItem.data
      toaster.success({
        text: `New record created - Item No: ${createdItem.ITEMNO}`,
      })
    }

    // Clear syncing status after a short delay to show the sync completed
    if (newItemId) {
      setTimeout(() => {
        store.setSyncingItem(newItemId, false)
      }, 2000)
    }

    // Close the form
    close()
  } catch (err: any) {
    toaster.error({
      text: err.message || 'Failed to add new item. Please try again.',
    })
  } finally {
    isSubmitting.value = false
  }
}

function close() {
  emit('close')
}
</script>
